import { isHybridSearchFlagEnabled } from './hybridSearchFlag';

/**
 * Check whether a search place represents a city all-terminals selection.
 * @param {Object} place - Search place to inspect.
 * @returns {boolean} Whether the place is a city all-terminals selection.
 */
export const isCityAllTerminalsPlace = (place) => Boolean(place?.id && !place?.cityName);

/**
 * Normalize a place name for duplicate-name comparisons.
 * @param {string} value - Place name to normalize.
 * @returns {string} Lowercase place name without accents.
 */
const normalizePlaceName = (value = '') =>
  `${value}`
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

/**
 * Convert a slug fragment into a display label.
 * @param {string} value - Slug fragment to titleize.
 * @returns {string} Titleized label.
 */
const titleizeSlug = (value = '') =>
  value
    .split('-')
    .filter(Boolean)
    .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(' ');

/**
 * Derive a terminal display name from a city-prefixed terminal slug.
 * @param {Object} place - Terminal place with id and city id metadata.
 * @returns {string} Terminal name derived from the slug, or an empty string.
 */
export const terminalNameFromSlug = (place) => {
  const id = place?.id || '';
  const cityId = place?.cityId || place?.city_id;
  if (!id.startsWith('t-') || !cityId) return '';

  const terminalSlug = id.replace(`t-${cityId}-`, '');
  if (terminalSlug === id) return '';

  return titleizeSlug(terminalSlug);
};

/**
 * Build a display label for a city or terminal place in result group headers.
 * @param {Object} place - Place returned by the search or trip result.
 * @returns {string} Human-readable place label.
 */
export const terminalResultPlaceLabel = (place) => {
  if (!place) return '';

  const city = place.cityName || place.stateName || place.state;
  const nameDuplicatesCity = city && normalizePlaceName(place.name) === normalizePlaceName(city);
  const terminalName = nameDuplicatesCity ? terminalNameFromSlug(place) : '';

  if (terminalName) return `${city}, ${terminalName}`;

  if (city && city !== place.name) return `${place.name}, ${city}`;

  return place.name || '';
};

/**
 * Decide whether trip results should be grouped by terminal.
 * @param {Object} params - Grouping conditions for the current results list.
 * @param {boolean|string} params.featureEnabled - Feature flag value for grouping.
 * @param {Object} params.origin - Selected origin place.
 * @param {Object} params.destination - Selected destination place.
 * @param {boolean} params.isHorizontal - Whether the list is rendered horizontally.
 * @param {boolean} params.isRecommendedList - Whether the list renders recommendations.
 * @returns {boolean} Whether terminal grouping should be enabled.
 */
export const shouldGroupTerminalResults = ({
  featureEnabled,
  origin,
  destination,
  isHorizontal,
  isRecommendedList,
}) =>
  isHybridSearchFlagEnabled(featureEnabled) &&
  !isHorizontal &&
  !isRecommendedList &&
  (isCityAllTerminalsPlace(origin) || isCityAllTerminalsPlace(destination));

/**
 * Resolve the origin and destination labels for a terminal result group.
 * @param {Object} params - Trip and selected route places.
 * @param {Object} params.trip - Trip used to resolve concrete terminals.
 * @param {Object} params.origin - Selected origin place.
 * @param {Object} params.destination - Selected destination place.
 * @returns {{originLabel: string, destinationLabel: string}} Labels for each route side.
 */
const getTerminalResultGroupPlaceLabels = ({ trip, origin, destination }) => {
  const groupByOrigin = isCityAllTerminalsPlace(origin);
  const groupByDestination = isCityAllTerminalsPlace(destination);
  const originPlace = groupByOrigin ? trip.origin : origin;
  const destinationPlace = groupByDestination ? trip.destination : destination;

  return {
    originLabel: terminalResultPlaceLabel(originPlace),
    destinationLabel: terminalResultPlaceLabel(destinationPlace),
  };
};

/**
 * Build the header label for a terminal result group.
 * @param {Object} params - Trip and selected route places.
 * @param {Object} params.trip - Trip used to resolve concrete terminals.
 * @param {Object} params.origin - Selected origin place.
 * @param {Object} params.destination - Selected destination place.
 * @returns {string} Route label for the group.
 */
export const getTerminalResultGroupLabel = ({ trip, origin, destination }) => {
  const { originLabel, destinationLabel } = getTerminalResultGroupPlaceLabels({
    trip,
    origin,
    destination,
  });

  return `${originLabel} - ${destinationLabel}`;
};

/**
 * Identify which side of the route should be highlighted in a group header.
 * @param {Object} params - Selected origin and destination places.
 * @param {Object} params.origin - Selected origin place.
 * @param {Object} params.destination - Selected destination place.
 * @returns {string|null} Highlighted route side.
 */
export const getTerminalResultGroupHighlight = ({ origin, destination }) => {
  if (isCityAllTerminalsPlace(origin)) return 'origin';
  if (isCityAllTerminalsPlace(destination)) return 'destination';

  return null;
};

/**
 * Group trips by the concrete terminal represented by a city all-terminals search side.
 * @param {Object} params - Trips and selected route places.
 * @param {Array<Object>} params.trips - Trips returned by search.
 * @param {Array<Object>} params.recommendedTrips - Recommended trips to place before regular trips.
 * @param {Object} params.origin - Selected origin place.
 * @param {Object} params.destination - Selected destination place.
 * @returns {Array<Object>} Ordered terminal result groups. Each group carries the joined `label`
 * (keys, aria, tests) plus separate `originLabel` and `destinationLabel` for rendering.
 */
export const groupTripsByTerminalResult = ({
  trips = [],
  recommendedTrips = [],
  origin,
  destination,
}) => {
  const groupByOrigin = isCityAllTerminalsPlace(origin);
  const groupByDestination = isCityAllTerminalsPlace(destination);
  const groupsByKey = new Map();
  const groups = [];
  const recommendedTripIds = new Set(recommendedTrips.map((trip) => trip?.id).filter(Boolean));
  const orderedTrips = [
    ...recommendedTrips,
    ...trips.filter((trip) => !trip?.id || !recommendedTripIds.has(trip.id)),
  ];

  orderedTrips.forEach((trip, index) => {
    if (!trip) return;

    if (trip.isAd || !trip.origin || !trip.destination) {
      groups.push({
        key: `ungrouped-${index}`,
        label: null,
        originLabel: null,
        destinationLabel: null,
        trips: [trip],
      });
      return;
    }

    const key = [
      groupByOrigin ? trip.originId : origin?.id,
      groupByDestination ? trip.destinationId : destination?.id,
    ].join('|');
    let group = groupsByKey.get(key);

    if (!group) {
      const { originLabel, destinationLabel } = getTerminalResultGroupPlaceLabels({
        trip,
        origin,
        destination,
      });

      group = {
        key,
        label: `${originLabel} - ${destinationLabel}`,
        originLabel,
        destinationLabel,
        highlight: getTerminalResultGroupHighlight({ origin, destination }),
        trips: [],
      };
      groupsByKey.set(key, group);
      groups.push(group);
    }

    group.trips.push(trip);
  });

  return groups;
};
